// @givmo/design-system — the video embed allowlist (Profile Enrichment plan §3.7).
//
// The ONE list of embed hosts a Givmo surface may ever put in an iframe, and the origins a
// Content-Security-Policy grants for them. It lives on its own React-free subpath
// (`@givmo/design-system/video-embed`) so a SERVER module — the donate app's CSP builder — can read
// the same constant the `VideoFacade` re-checks before it mounts a frame, without pulling the
// component library into a server bundle. The server canonicalizes every stored video URL to one of
// these two forms; a client re-validates before an iframe ever mounts. Widening the list is
// Jonathan's call only (plan §1.2).

/** The two embed hosts a video block may play from — re-checked before an iframe ever mounts. */
export const CANONICAL_VIDEO_EMBED_PREFIXES = [
  'https://www.youtube-nocookie.com/embed/',
  'https://player.vimeo.com/video/',
] as const;

/**
 * The CSP `frame-src` sources for those hosts — derived from the prefixes, never typed twice, so
 * the policy and the facade cannot drift apart.
 */
export const CANONICAL_VIDEO_EMBED_ORIGINS: readonly string[] = CANONICAL_VIDEO_EMBED_PREFIXES.map(
  (prefix) => new URL(prefix).origin,
);

/** Whether a value is an embed URL on one of the allowed hosts (the canonical two by default). */
export function isCanonicalVideoEmbedUrl(
  value: string | null | undefined,
  allowedPrefixes: readonly string[] = CANONICAL_VIDEO_EMBED_PREFIXES,
): boolean {
  return typeof value === 'string' && allowedPrefixes.some((prefix) => value.startsWith(prefix));
}

/** The provider a canonical embed URL plays from, in the words a person knows it by; null otherwise. */
export function videoProviderLabel(value: string | null | undefined): 'YouTube' | 'Vimeo' | null {
  if (typeof value !== 'string') return null;
  if (value.startsWith(CANONICAL_VIDEO_EMBED_PREFIXES[0])) return 'YouTube';
  if (value.startsWith(CANONICAL_VIDEO_EMBED_PREFIXES[1])) return 'Vimeo';
  return null;
}
